import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { 
  createUserWithEmailAndPassword, 
  updateProfile,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  doc, 
  setDoc,
  getDoc, 
  serverTimestamp,
  collection,
  addDoc 
} from 'firebase/firestore';
import { auth, db } from '../../configs/firebase';
import { EAuthData } from '../Provider/EAuthProvider';
import { Logo } from '../../components/images';
import { 
  FaBuilding, 
  FaGlobe, 
  FaFileUpload, 
  FaCheck, 
  FaExclamationTriangle,
  FaInfoCircle,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaUsers,
  FaLinkedin,
  FaTwitter,
  FaInstagram,
  FaEye, 
  FaEyeSlash
} from 'react-icons/fa';

const EmployerSignup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setSignupInProgressFlag } = EAuthData();

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

  // Step-specific validation schemas
  const getValidationSchema = (currentStep) => {
    switch (currentStep) {
      case 1:
        // Company Information (unchanged)
        return Yup.object({
          companyName: Yup.string()
            .required('Company name is required')
            .min(2, 'Company name must be at least 2 characters')
            .max(100, 'Company name must be less than 100 characters'),
          
          legalName: Yup.string()
            .required('Legal company name is required')
            .min(2, 'Legal name must be at least 2 characters'),
          
          industry: Yup.string()
            .required('Industry is required'),
          
          companySize: Yup.string()
            .required('Company size is required'),
          
          foundedYear: Yup.number()
            .required('Founded year is required')
            .min(1800, 'Invalid year')
            .max(new Date().getFullYear(), 'Founded year cannot be in the future'),
          
          website: Yup.string()
            .test('url', 'Invalid website URL', function(value) {
              if (!value || value.trim() === '') return true; // Optional field
              return urlRegex.test(value);
            }),
          
          linkedinProfile: Yup.string()
            .test('url', 'Invalid LinkedIn URL', function(value) {
              if (!value || value.trim() === '') return true; // Optional field
              return urlRegex.test(value);
            }),
          
          otherSocialProfile: Yup.string()
            .test('url', 'Invalid URL', function(value) {
              if (!value || value.trim() === '') return true; // Optional field
              return urlRegex.test(value);
            })
        });

      case 2:
        // Contact & Address Information (NEW)
        return Yup.object({
          // Contact fields
          email: Yup.string()
            .matches(emailRegex, 'Invalid email address')
            .required('Email address is required'),
          
          contactPersonName: Yup.string()
            .required('Contact person name is required')
            .min(2, 'Name must be at least 2 characters'),
          
          contactPersonTitle: Yup.string()
            .required('Contact person title is required'),
          
          phone: Yup.string()
            .matches(phoneRegex, 'Invalid phone number')
            .required('Phone number is required'),
          
          alternativePhone: Yup.string()
            .test('phone', 'Invalid phone number', function(value) {
              if (!value || value.trim() === '') return true; // Optional field
              return phoneRegex.test(value);
            }),

          // Address fields
          streetAddress: Yup.string()
            .required('Street address is required')
            .min(5, 'Street address must be at least 5 characters'),
          
          city: Yup.string()
            .required('City is required')
            .min(2, 'City must be at least 2 characters'),
          
          state: Yup.string()
            .required('State/Province is required'),
          
          postalCode: Yup.string()
            .required('Postal code is required')
            .min(3, 'Postal code must be at least 3 characters'),
          
          country: Yup.string()
            .required('Country is required')
        });

      case 3:
        // Password & Legal (NEW)
        return Yup.object({
          password: Yup.string()
            .min(8, 'Password must be at least 8 characters')
            .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number')
            .required('Password is required'),
          
          confirmPassword: Yup.string()
            .test('passwords-match', 'Passwords must match', function(value) {
              return this.parent.password === value;
            })
            .required('Confirm password is required'),
          
          termsAccepted: Yup.boolean()
            .oneOf([true], 'You must accept the terms and conditions'),
              
          privacyAccepted: Yup.boolean()
            .oneOf([true], 'You must accept the privacy policy'),
          
          backgroundCheckConsent: Yup.boolean()
            .oneOf([true], 'You must consent to background verification')
        });

      default:
        return Yup.object({});
    }
  };

  const initialValues = {
    // Step 1: Company Information
    companyName: '',
    legalName: '',
    industry: '',
    companySize: '',
    foundedYear: '',
    website: '',
    linkedinProfile: '',
    otherSocialProfile: '',

    // Step 2: Contact Information
    email: '',
    password: '',
    confirmPassword: '',
    contactPersonName: '',
    contactPersonTitle: '',
    phone: '',
    alternativePhone: '',

    // Step 3: Address Information
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    termsAccepted: false,
    privacyAccepted: false,
    backgroundCheckConsent: false
  };

  const industryOptions = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing',
    'Retail', 'Consulting', 'Media & Entertainment', 'Non-profit',
    'Government', 'Real Estate', 'Transportation', 'Energy', 'Other'
  ];

  const companySizeOptions = [
    '1-10 employees', '11-50 employees', '51-200 employees',
    '201-500 employees', '501-1000 employees', '1000+ employees'
  ];

  const countryOptions = [
    'Ghana', 'Other'
  ];

  // Risk assessment function
  const assessRiskLevel = (values) => {
    let riskScore = 0;
    
    // Company age assessment
    const currentYear = new Date().getFullYear();
    const companyAge = currentYear - Number(values.foundedYear);
    
    if (companyAge < 1) riskScore += 2; // Very new company
    else if (companyAge < 3) riskScore += 1; // New company
    // Established companies (3+ years) get 0 points
    
    // Company size assessment
    const size = values.companySize;
    if (size === '1-10 employees') riskScore += 1;
    // Larger companies get 0 points
    
    // Website assessment
    if (!values.website || values.website.trim() === '') {
      riskScore += 1; // No website increases risk
    }
    
    // LinkedIn profile assessment
    if (!values.linkedinProfile || values.linkedinProfile.trim() === '') {
      riskScore += 1; // No LinkedIn increases risk
    }
    
    // Industry assessment (some industries might be higher risk)
    const highRiskIndustries = ['Other'];
    if (highRiskIndustries.includes(values.industry)) {
      riskScore += 1;
    }
    
    // Determine risk level
    if (riskScore === 0) return 'low';
    else if (riskScore <= 2) return 'moderate';
    else return 'high';
  };

  const submitApplication = async (values) => {
    setLoading(true);
    console.log('🚀 Starting employer signup with auth protection...');
    
    try {
      // Step 1: Set signup flag to prevent auth interference
      setSignupInProgressFlag(true);
      console.log('🛡️ Signup protection activated');

      // Step 2: Create Firebase user
      console.log('👤 Creating Firebase user...');
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        values.email, 
        values.password
      );
      const firebaseUser = userCredential.user;
      console.log('✅ Firebase user created successfully:', firebaseUser.uid);

      // Step 3: Update profile
      console.log('📝 Updating user profile...');
      await updateProfile(firebaseUser, {
        displayName: values.companyName
      });
      console.log('✅ Profile updated successfully');

      // Step 4: Assess risk level
      const riskLevel = assessRiskLevel(values);
      console.log('🔍 Risk assessment:', riskLevel);

      // Step 5: Prepare status
      let applicationStatus, verificationStatus, isActive, canLogin;
      
      if (riskLevel === 'low') {
        applicationStatus = 'submitted';
        verificationStatus = 'pending';
        isActive = true;
        canLogin = true;
      } else {
        applicationStatus = 'incomplete';
        verificationStatus = 'pending';
        isActive = false;
        canLogin = true;
      }

      console.log('📊 Status determined:', { applicationStatus, verificationStatus, riskLevel });

      // Step 6: Create EMPLOYER PROFILE document
      console.log('👔 Creating employer profile document...');
      
      const employerProfileData = {
        uid: firebaseUser.uid,
        companyName: values.companyName,
        legalName: values.legalName,
        email: values.email,
        industry: values.industry,
        companySize: values.companySize,
        foundedYear: values.foundedYear,
        website: values.website || '',
        linkedinProfile: values.linkedinProfile || '',
        otherSocialProfile: values.otherSocialProfile || '',
        contactPersonName: values.contactPersonName,
        contactPersonTitle: values.contactPersonTitle,
        phone: values.phone,
        alternativePhone: values.alternativePhone || '',
        streetAddress: values.streetAddress,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
        country: values.country,
        verificationStatus: verificationStatus,
        applicationStatus: applicationStatus,
        isActive: isActive,
        canLogin: canLogin,
        riskLevel: riskLevel,
        registrationPhase: riskLevel === 'low' ? 'completed' : 'awaiting_documents',
        appliedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      console.log('🔥 Creating employers document...');
      await setDoc(doc(db, 'employers', firebaseUser.uid), employerProfileData);
      console.log('✅ Employer profile created successfully!');

      // Step 7: Create application document
      const employerApplicationData = {
        uid: firebaseUser.uid, // Admin queries by uid
        employerId: firebaseUser.uid, // Backup reference
        companyName: values.companyName,
        legalName: values.legalName,
        email: values.email,
        industry: values.industry,
        companySize: values.companySize,
        foundedYear: values.foundedYear,
        website: values.website || '',
        linkedinProfile: values.linkedinProfile || '',
        
        // Contact Information
        contactPersonName: values.contactPersonName,
        contactPersonTitle: values.contactPersonTitle,
        phone: values.phone,
        alternativePhone: values.alternativePhone || '',
        
        // Address Information  
        streetAddress: values.streetAddress,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
        country: values.country,
        
        // Application Status & Verification
        applicationStatus: applicationStatus,
        verificationStatus: verificationStatus,
        registrationPhase: riskLevel === 'low' ? 'completed' : 'awaiting_documents',
        riskLevel: riskLevel,
        isActive: isActive,
        canLogin: canLogin,
        
        // Document Status (for admin tracking)
        documentsRequired: riskLevel !== 'low',
        documentsProvided: {
            businessLicense: false,
            taxDocument: false,
            companyProfile: false
          },
        documentUrls: {
          businessLicenseUrl: '',
          taxDocumentUrl: '',
          companyProfileUrl: ''
        },
        
        // Timestamps
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        
        // Admin Notes (empty initially)
        verificationNotes: [],
        adminNotes: ''
      };

      try {
        const applicationDocRef = await addDoc(collection(db, 'employer_applications'), employerApplicationData);
        console.log('✅ Application document created:', applicationDocRef.id);
      } catch (appError) {
        console.error('⚠️ Application document creation failed, but continuing:', appError);
        // Continue without application document
      }

      // Step 8: Clear signup flag
      setTimeout(() => {
        setSignupInProgressFlag(false);
        console.log('🛡️ Signup protection deactivated');
      }, 2000);

      // Step 9: Success
      console.log('🎉 Employer registration completed successfully!');
      
      setTimeout(() => {
        if (riskLevel === 'low' || isActive === true) {
          alert('Registration completed! Your account has been automatically approved.');
          navigate('/employer/');
        } else {
          alert('Registration successful! Please complete document verification to activate your account.');
          navigate('/employer/application-submitted');
        }
      }, 1000);

    } catch (error) {
      console.error('❌ Employer registration error:', error);
      
      // Clear signup flag on error
      setSignupInProgressFlag(false);
      
      let userMessage = 'Error during registration: ' + error.message;
      
      if (error.code === 'auth/email-already-in-use') {
        userMessage = 'An account with this email already exists.';
      } else if (error.code === 'auth/weak-password') {
        userMessage = 'Password is too weak. Please use a stronger password.';
      }
      
      alert(userMessage);
    } finally {
      setLoading(false);
    }
  };

  // Validate current step fields
  const validateCurrentStep = async (values, currentStep) => {
    try {
      const schema = getValidationSchema(currentStep);
      await schema.validate(values, { abortEarly: false });
      return { isValid: true, errors: {} };
    } catch (validationErrors) {
      const errors = {};
      validationErrors.inner.forEach(error => {
        errors[error.path] = error.message;
      });
      return { isValid: false, errors };
    }
  };

  const renderStep = (values, setFieldValue, errors, touched) => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Company Information</h3>
              <p className="text-gray-600">Tell us about your company</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <Field
                  name="companyName"
                  type="text"
                  placeholder="e.g., Tech Innovations Inc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                />
                <ErrorMessage name="companyName" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Legal Company Name *
                </label>
                <Field
                  name="legalName"
                  type="text"
                  placeholder="Legal name as registered"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                />
                <ErrorMessage name="legalName" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry *
                </label>
                <Field
                  as="select"
                  name="industry"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                >
                  <option value="">Select Industry</option>
                  {industryOptions.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </Field>
                <ErrorMessage name="industry" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Size *
                </label>
                <Field
                  as="select"
                  name="companySize"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                >
                  <option value="">Select Company Size</option>
                  {companySizeOptions.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </Field>
                <ErrorMessage name="companySize" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Founded Year *
                </label>
                <Field
                  name="foundedYear"
                  type="number"
                  placeholder="e.g., 2020"
                  min="1800"
                  max={new Date().getFullYear()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                />
                <ErrorMessage name="foundedYear" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website
                </label>
                <Field
                  name="website"
                  type="url"
                  placeholder="https://yourcompany.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                />
                <ErrorMessage name="website" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LinkedIn Profile
                </label>
                <Field
                  name="linkedinProfile"
                  type="url"
                  placeholder="https://linkedin.com/company/yourcompany"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                />
                <ErrorMessage name="linkedinProfile" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Other Social Profile
                </label>
                <Field
                  name="otherSocialProfile"
                  type="url"
                  placeholder="Twitter, Instagram, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                />
                <ErrorMessage name="otherSocialProfile" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Contact & Address Information</h3>
              <p className="text-gray-600">How can we reach you and where are you located?</p>
            </div>

            {/* Contact Information Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaEnvelope className="mr-2" />
                Contact Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Field
                    name="email"
                    type="email"
                    placeholder="your.email@company.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person Name *
                  </label>
                  <Field
                    name="contactPersonName"
                    type="text"
                    placeholder="Full name of contact person"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="contactPersonName" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Person Title *
                  </label>
                  <Field
                    name="contactPersonTitle"
                    type="text"
                    placeholder="e.g., HR Manager, CEO"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="contactPersonTitle" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Field
                    name="phone"
                    type="tel"
                    placeholder="+1234567890"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="phone" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alternative Phone
                  </label>
                  <Field
                    name="alternativePhone"
                    type="tel"
                    placeholder="Alternative contact number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="alternativePhone" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaMapMarkerAlt className="mr-2" />
                Business Address
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <Field
                    name="streetAddress"
                    type="text"
                    placeholder="123 Business Street"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="streetAddress" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <Field
                    name="city"
                    type="text"
                    placeholder="City"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="city" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State/Province *
                  </label>
                  <Field
                    name="state"
                    type="text"
                    placeholder="State or Province"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="state" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code *
                  </label>
                  <Field
                    name="postalCode"
                    type="text"
                    placeholder="12345"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  />
                  <ErrorMessage name="postalCode" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <Field
                    as="select"
                    name="country"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200"
                  >
                    <option value="">Select Country</option>
                    {countryOptions.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </Field>
                  <ErrorMessage name="country" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Security & Legal</h3>
              <p className="text-gray-600">Set up your account security and accept our terms</p>
            </div>

            {/* Password Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaPhone className="mr-2" />
                Account Security
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <Field
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200'
                        placeholder="Enter a secure password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                          <FaEyeSlash className="h-5 w-5 text-gray-400" />
                      ) : (
                          <FaEye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                      Must be at least 8 characters with uppercase, lowercase, and number
                  </p>
                  <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password *
                  </label>
                  <div className="relative">
                    <Field
                        type={showPassword ? "text" : "password"}
                        name="confirmPassword"
                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition duration-200'
                        placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                          <FaEyeSlash className="h-5 w-5 text-gray-400" />
                      ) : (
                          <FaEye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-sm mt-1" />
                </div>
              </div>
            </div>

            {/* Legal Agreements */}
            <div className="border-t border-gray-200 pt-6">
              <div className="space-y-4">
                <label className="flex items-start space-x-3">
                  <Field
                    type="checkbox"
                    name="termsAccepted"
                    className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">
                    I accept the <Link to="/terms" className="text-orange-600 hover:underline">Terms and Conditions</Link> *
                  </span>
                </label>
                <ErrorMessage name="termsAccepted" component="div" className="text-red-500 text-sm" />

                <label className="flex items-start space-x-3">
                  <Field
                    type="checkbox"
                    name="privacyAccepted"
                    className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">
                    I accept the <Link to="/privacy" className="text-orange-600 hover:underline">Privacy Policy</Link> *
                  </span>
                </label>
                <ErrorMessage name="privacyAccepted" component="div" className="text-red-500 text-sm" />

                <label className="flex items-start space-x-3">
                  <Field
                    type="checkbox"
                    name="backgroundCheckConsent"
                    className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">
                    I consent to background verification and due diligence checks *
                  </span>
                </label>
                <ErrorMessage name="backgroundCheckConsent" component="div" className="text-red-500 text-sm" />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepValidation = (step) => {
    const stepFields = {
      1: ['companyName', 'legalName', 'industry', 'companySize', 'foundedYear'],
      2: ['email', 'contactPersonName', 'contactPersonTitle', 'phone', 'streetAddress', 'city', 'state', 'postalCode', 'country'],
      3: ['password', 'confirmPassword', 'termsAccepted', 'privacyAccepted', 'backgroundCheckConsent']
    };
    return stepFields[step] || [];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-200 to-orange-500 p-4 md:p-10">
      {/* Main Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8'>
          <Link to="/main-signup">
              <div className='flex flex-row items-center mb-4 sm:mb-0'>
                  <img src={Logo} className='w-8 h-8 sm:w-10 sm:h-10' alt="Logo"/>
                  <p className='text-lg sm:text-xl lg:text-2xl ml-2 font-medium text-gray-800'>Oodles Internships</p>
              </div>
          </Link>
          
          <div className='flex sm:flex-row justify-center gap-2 sm:gap-3'>               
              <Link to='/about'                             
              className='text-md sm:text-md hover:underline'               
              >                 
                  About               
              </Link>               
              <Link to='/faq'                              
              className='text-md sm:text-md hover:underline'               
              >                 
                  FAQs               
              </Link>             
          </div>
      </div>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl text-gray-800 mb-2">Employer Registration</h2>
          <p className="text-gray-600">Join our platform and connect with talented students</p>
        </div>

        {/* Progress Bar - Fixed to 3 steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3].map((stepNumber) => (
              <div
                key={stepNumber}
                className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm ${
                  stepNumber <= step
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-300 text-gray-600'
                }`}
              >
                {stepNumber}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2">
            <div
              className="bg-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>Company Info</span>
            <span>Contact & Address</span>
            <span>Security & Legal</span>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <Formik
            initialValues={initialValues}
            validationSchema={Yup.object({})} // Start with empty schema
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            enableReinitialize={true}
            onSubmit={(values, actions) => {
              if (step === 3) {
                submitApplication(values);
              }
            }}
          >
            {({ values, setFieldValue, errors, touched, isSubmitting, validateForm, setErrors, setTouched }) => (
              <Form>
                {renderStep(values, setFieldValue, errors, touched)}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                  <div>
                    {step > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          setStep(step - 1);
                          // Clear errors when going back
                          setErrors({});
                          setTouched({});
                        }}
                        className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                        disabled={loading}
                      >
                        Previous
                      </button>
                    )}
                  </div>

                  <div className="flex space-x-4">
                    {step < 3 ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const validation = await validateCurrentStep(values, step);
                          
                          if (validation.isValid) {
                            setStep(step + 1);
                            // Clear any existing errors when moving to next step
                            setErrors({});
                            setTouched({});
                          } else {
                            // Set errors and touched for current step fields only
                            setErrors(validation.errors);
                            const stepFields = getStepValidation(step);
                            const touchedFields = {};
                            stepFields.forEach(field => {
                              touchedFields[field] = true;
                            });
                            setTouched(touchedFields);
                          }
                        }}
                        className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 font-medium"
                        disabled={loading}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="button" // Changed from type="submit"
                        onClick={async () => {
                          const validation = await validateCurrentStep(values, step);
                          
                          if (validation.isValid) {
                            submitApplication(values);
                          } else {
                            // Set errors and touched for current step fields only
                            setErrors(validation.errors);
                            const stepFields = getStepValidation(step);
                            const touchedFields = {};
                            stepFields.forEach(field => {
                              touchedFields[field] = true;
                            });
                            setTouched(touchedFields);
                          }
                        }}
                        disabled={loading || isSubmitting}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading || isSubmitting ? 'Submitting...' : 'Complete Registration'}
                      </button>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default EmployerSignup;